import { useEffect, useRef, useState } from 'react';
import { Alert, LinearProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { DocumentFrameProps } from 'types/documentEditing';
import { getDocumentMessage, postDocumentSession, sendDocumentFocus, sendDocumentHostReady } from 'utils/documentEditing/documentFrame';
import { EditorFrame } from '../DocumentEditor.styles';

export const DocumentFrame = ({ session, origin, title, takeLaunchSession, overlayOpen = false }: DocumentFrameProps) => {
  const { t } = useTranslation();
  const frameRef = useRef<HTMLIFrameElement>(null);
  const submitted = useRef(false);
  const [status, setStatus] = useState<'loading' | 'ready' | 'slow' | 'failed'>('loading');
  const [hostOverlayOpen, setHostOverlayOpen] = useState(false);

  useEffect(() => {
    const host = frameRef.current?.closest('.MuiModal-root');
    if (!host) return;
    const updateOverlay = () => setHostOverlayOpen(host.getAttribute('aria-hidden') === 'true');
    updateOverlay();
    const observer = new MutationObserver(updateOverlay);
    observer.observe(host, { attributes: true, attributeFilter: ['aria-hidden'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (frameRef.current && (session.canEdit || session.canReview)) sendDocumentFocus(frameRef.current, origin, overlayOpen || hostOverlayOpen);
  }, [origin, overlayOpen, hostOverlayOpen, session.canEdit, session.canReview, status]);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const receiveMessage = (event: MessageEvent) => {
      if (getDocumentMessage(event, frame, origin) !== 'App_LoadingStatus') return;
      setStatus('ready');
    };
    window.addEventListener('message', receiveMessage);
    const timeout = window.setTimeout(() => setStatus((value) => (value === 'loading' ? 'slow' : value)), 60000);
    if (!submitted.current) {
      const credentials = takeLaunchSession();
      if (credentials) {
        submitted.current = true;
        try {
          postDocumentSession(frame, credentials);
        } catch {
          setStatus('failed');
        }
      } else {
        setStatus('failed');
      }
    }
    return () => {
      window.removeEventListener('message', receiveMessage);
      window.clearTimeout(timeout);
    };
  }, [origin, takeLaunchSession]);

  return (
    <>
      {status === 'loading' && <LinearProgress aria-label={t('user.documentEditor.loading')} />}
      {status === 'slow' && <Alert severity="warning">{t('user.documentEditor.slowLoading')}</Alert>}
      {status === 'failed' && <Alert severity="error">{t('user.documentEditor.unavailable')}</Alert>}
      <EditorFrame
        ref={frameRef}
        name={`onlyoffice-${session.sessionId}`}
        src="about:blank"
        title={title}
        referrerPolicy="no-referrer"
        allow="clipboard-read; clipboard-write; fullscreen"
        allowFullScreen
        onLoad={() => {
          if (frameRef.current) sendDocumentHostReady(frameRef.current, origin);
        }}
      />
    </>
  );
};
